import { Injectable } from '@angular/core';
import { PaymentMethod, PaymentMethods, PaymentMethodsType, ShipmentInfo } from '@cargos/sprintpay-models';
import { PaymentMethodsRequestService } from '@cargos/sprintpay-services';
import { Observable, combineLatest, distinctUntilChanged, map, of, switchMap, take } from 'rxjs';
import { PaymentMethodSelected } from '../../modules/cart/lib-cart/balance/shared/models';
import { PaymentMethodsHandlerService } from '../../modules/cart/lib-cart/shared/services/payment-methods-handler.service';
import { CartBillService } from '../cart.service';
import { ConfigService } from '../config.service';
import { CurrentFacilityService } from '../current-facility.service';
import { CustomerService } from '../customer.service';
import { PaymentMethodsService } from '../payment-methods.service';
import { SessionService } from '../session.service';
import { DestinationNeededResponseType } from './models/destinations';
import { FeatureFAQ } from './models/feature-faq';
import { CargoCreditValidations } from './models/types';
@Injectable({
    providedIn: 'root',
})
export class FeatureRestrictionsService {
    private paymentMethodsAllowedAboveThreshold: string[] = [
        PaymentMethods.CREDIT_CARD,
        PaymentMethods.ECHECK,
        PaymentMethods.FINANCE,
        PaymentMethods.SPRINTPAY_FLEX,
    ];

    constructor(
        private paymentMethodsRequestService: PaymentMethodsRequestService,
        private customerService: CustomerService,
        private configService: ConfigService,
        private currentFacilityService: CurrentFacilityService,
        private paymentMethodsService: PaymentMethodsService,
        private cartBillService: CartBillService,
        private paymentMethodsHandlerService: PaymentMethodsHandlerService,
        private sessionService: SessionService
    ) {}

    isPaypalPaymentMethodAvailable(): Observable<boolean> {
        return this.paymentMethodsRequestService.getPaymentMethods().pipe(
            map((paymentMethods: PaymentMethod[]) => {
                return paymentMethods.some((paymentMethod) => paymentMethod.name === PaymentMethods.PAYPAL);
            })
        );
    }

    isAccountOnHold(): Observable<boolean> {
        return this.customerService.getCurrentUser().pipe(
            map((customer) => {
                const customerProfile = customer?.customerProfile;

                return customerProfile?.name === 'Account on Credit Hold' || !!customer?.isCreditHold;
            })
        );
    }

    isFAQAvailable(): FeatureFAQ {
        const isFAQEnable = this.configService.isEnableFAQPDF();
        const faqURLs = this.currentFacilityService.getFAQExternalURL();

        return {
            isEnabled: !!(isFAQEnable && (faqURLs?.externalURL || faqURLs?.pdfURL)),
            externalURL: faqURLs?.externalURL,
            pdfURL: faqURLs?.pdfURL,
        };
    }

    isDestinationNeeded(payment: ShipmentInfo): Observable<DestinationNeededResponseType> {
        return of(this.configService.forceToRequestDestination()).pipe(
            switchMap((isDestinationNeeded) => {
                if (!isDestinationNeeded) {
                    return of({
                        isDestinationNeeded: false,
                    });
                }

                return of({
                    isDestinationNeeded: !payment.shipmentDetails?.destination,
                    destination: payment.shipmentDetails?.destination,
                });
            })
        );
    }

    isAllowedToPayWithCredit$(
        paymentMethod: PaymentMethodsType = PaymentMethods.CARGO_CREDIT,
        isAboveThreshold?: boolean
    ): Observable<CargoCreditValidations> {
        if (!this.paymentMethodsService.isPaymentMethodAvailable(paymentMethod, isAboveThreshold)) {
            return of({
                isAllowed: false,
            });
        }

        return this.isEnoughCredit$(paymentMethod, isAboveThreshold).pipe(
            map((isEnoughCredit) => {
                return {
                    isAllowed: isEnoughCredit,
                    isThereInvoicesInCart: false,
                    isEnoughCredit,
                };
            })
        );
    }

    isEnoughCredit$(
        paymentMethod: PaymentMethodsType = PaymentMethods.CARGO_CREDIT,
        isAboveThreshold?: boolean
    ): Observable<boolean> {
        return this.paymentMethodsService.getCreditAvailableByPaymentMethod$(paymentMethod).pipe(
            switchMap((creditAvailable) => {
                return this.cartBillService
                    .getTotalByThreshold$(isAboveThreshold)
                    .pipe(map((total: number) => creditAvailable > total));
            }),
            distinctUntilChanged()
        );
    }

    isInvoiceCheckLimitValid$(): Observable<boolean> {
        const uniqueRequestor = this.customerService.isUniqueRequestor();
        const invoiceCheckLimit = this.customerService.getInvoiceCheckLimit();

        return this.cartBillService.isTherePaymentsRequestsNoInvoices$().pipe(
            map((isTherePaymentsRequestsNoInvoices) => {
                const subtotal = this.cartBillService.getSubtotal();

                if (!invoiceCheckLimit || !isTherePaymentsRequestsNoInvoices || uniqueRequestor) {
                    return true;
                }

                return subtotal < invoiceCheckLimit;
            })
        );
    }

    hasFinanceDisclaimer(paymentMethod: string, aboveThreshold: boolean): Observable<string> {
        return combineLatest([
            this.cartBillService.getLendingTerms$(aboveThreshold),
            this.customerService.hasAutomatedDebits$(),
        ]).pipe(
            map(([lendingTerms, automatedDebits]) => {
                const isAutomatedDebitsEnabled = !!automatedDebits.customerAccountAutomatedDebit;
                if (paymentMethod !== PaymentMethods.SPRINTPAY_FLEX || !lendingTerms) {
                    return '';
                }
                return isAutomatedDebitsEnabled
                    ? `Your bank account will be <span class="fw-bold"> debited ${lendingTerms} days after </span> this Payment Request is processed.`
                    : `Payment is due <span class="fw-bold"> in ${lendingTerms} days</span>.`;
            }),
            distinctUntilChanged()
        );
    }
    isPaymentMethodValidForPaymentsAboveThreshold(paymentMethod: PaymentMethodsType): boolean {
        return !!this.paymentMethodsAllowedAboveThreshold.find(
            (paymentMethodAboveThreshold) => paymentMethodAboveThreshold === paymentMethod
        );
    }

    getPaymentMethodsAllowedAboveThreshold(): string[] {
        return this.paymentMethodsAllowedAboveThreshold;
    }

    isPaymentMethodValid$(): Observable<boolean> {
        return this.cartBillService.isCartAboveThreshold$().pipe(
            switchMap((isCartAboveThreshold) => {
                if (isCartAboveThreshold && this.cartBillService.getCartPaymentRequests(false).length) {
                    return this.arePaymentMethodsSelectedValid$().pipe(
                        map((isPaymentMethodValid) => isPaymentMethodValid)
                    );
                }

                if (isCartAboveThreshold) {
                    return this.isPaymentMethodSelectedValid$(true);
                }

                return this.isPaymentMethodSelectedValid$(false);
            }),
            distinctUntilChanged()
        );
    }

    private arePaymentMethodsSelectedValid$(): Observable<boolean> {
        return combineLatest([
            this.isPaymentMethodSelectedValid$(false),
            this.isPaymentMethodSelectedValid$(true),
        ]).pipe(
            map(([paymentMethodSelected, paymentMethodSelectedAboveThreshold]) => {
                return paymentMethodSelected && paymentMethodSelectedAboveThreshold;
            }),
            distinctUntilChanged()
        );
    }

    isPaymentMethodSelectedValid$(aboveThreshold?: boolean): Observable<boolean> {
        return this.paymentMethodsHandlerService.getPaymentMethodSelected$(aboveThreshold).pipe(
            switchMap((paymentMethodSelected: PaymentMethodSelected | null) => {
                return this.sessionService.isAuthenticated$().pipe(
                    take(1),
                    switchMap((isAuthenticated) => {
                        if (!isAuthenticated) {
                            return this.paymentMethodsHandlerService.isPaymentMethodGuestValid$();
                        }

                        return this.isPaymentMethodAuthenticatedValid$(paymentMethodSelected, aboveThreshold);
                    })
                );
            }),
            distinctUntilChanged()
        );
    }

    private isPaymentMethodAuthenticatedValid$(
        paymentMethodSelected: PaymentMethodSelected | null,
        aboveThreshold?: boolean
    ): Observable<boolean> {
        return of(paymentMethodSelected).pipe(
            switchMap((paymentMethodSelected: PaymentMethodSelected | null) => {
                if (!paymentMethodSelected?.method) {
                    return of(false);
                }

                if (
                    aboveThreshold &&
                    !this.isPaymentMethodValidForPaymentsAboveThreshold(paymentMethodSelected.method)
                ) {
                    return of(false);
                }

                if (PaymentMethod.isCreditMethod(paymentMethodSelected.method as PaymentMethodsType)) {
                    return this.isAllowedToPayWithCredit$(paymentMethodSelected.method, aboveThreshold).pipe(
                        map((isAllowedToPayWithCredit) => isAllowedToPayWithCredit.isAllowed)
                    );
                }

                if (
                    paymentMethodSelected.method === PaymentMethods.CREDIT_CARD ||
                    paymentMethodSelected.method === PaymentMethods.ECHECK
                ) {
                    return of(!!paymentMethodSelected.token);
                }

                return of(true);
            }),
            distinctUntilChanged()
        );
    }

    isEnableToPayMultipleFacilities(): boolean {
        const isPaidToMultipleFacilitiesEnabled = this.configService.isPaidToMultipleFacilitiesEnable();
        const options = this.configService.getPaidToFacilitiesId() || [];

        return isPaidToMultipleFacilitiesEnabled && options.length > 1;
    }
}
